import { useState, FormEvent } from 'react';

// const FORM_URL = 'https://readdy.ai/api/form/d76r74tr5g02gnd6a5l0';
const FORM_URL = 'https://greenlyelectronicsrecycling.com/api/resend-readyai-form.php' ;
const serviceOptions = [
  'Electronics Recycling',
  'Hard Drive Shredding',
  'Guaranteed Data Destruction',
  'Cell Phone Destruction',
  'E-Waste Pickup',
  'Tape & Product Destruction',
  'General Inquiry',
];

interface FormState {
  name: string;
  email: string;
  phone: string;
  company: string;
  service: string;
  message: string;
  website_url?: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  service?: string;
  message?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[\d\s\-().+]{7,20}$/;

const validate = (form: FormState): FormErrors => {
  const errors: FormErrors = {};

  if (!form.name.trim()) {
    errors.name = 'Full name is required.';
  } else if (form.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters.';
  }

  if (!form.email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!EMAIL_RE.test(form.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }

  if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) {
    errors.phone = 'Please enter a valid phone number.';
  }

  if (!form.service) {
    errors.service = 'Please select a service.';
  }

  if (!form.message.trim()) {
    errors.message = 'Message is required.';
  } else if (form.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters.';
  } else if (form.message.length > 500) {
    errors.message = 'Message cannot exceed 500 characters.';
  }

  return errors;
};

const FieldError = ({ msg }: { msg?: string }) =>
  msg ? (
    <p className="flex items-center gap-1.5 text-red-500 text-xs mt-1.5">
      <i className="ri-error-warning-line shrink-0"></i>
      {msg}
    </p>
  ) : null;

const inputClass = (hasError: boolean) =>
  `w-full px-4 py-3 border rounded-xl text-sm text-gray-800 outline-none transition-colors ${
    hasError
      ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
      : 'border-gray-200 focus:border-[#1B6B3A] focus:ring-2 focus:ring-[#1B6B3A]/10'
  }`;

const ContactForm = () => {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    website_url: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const charCount = form.message.length;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };
    setForm(updated);
    // Re-validate touched field live
    if (touched[name as keyof FormState]) {
      const newErrors = validate(updated);
      setErrors((prev) => ({ ...prev, [name]: newErrors[name as keyof FormErrors] }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const newErrors = validate(form);
    setErrors((prev) => ({ ...prev, [name]: newErrors[name as keyof FormErrors] }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Mark all fields touched and run full validation
    setTouched({ name: true, email: true, phone: true, service: true, message: true });
    const newErrors = validate(form);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Honeypot check — bots fill this, real users don't see it
    if (form.website_url && form.website_url.trim() !== '') {
      setStatus('success');
      setForm({ name: '', email: '', phone: '', company: '', service: '', message: '', website_url: '' });
      setErrors({});
      setTouched({});
      return;
    }

    setStatus('loading');

    const body = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => {
      if (k !== 'website_url') body.append(k, v as string);
    });
    body.append('page', 'contact');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', company: '', service: '', message: '' });
        setErrors({});
        setTouched({});
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <form
      id="contact-form"
      onSubmit={handleSubmit}
      noValidate
      className="bg-white rounded-2xl p-8"
    >
      <input type="hidden" name="page" value="contact" />
      {/* Honeypot field — hidden from real users, bots will fill it */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none', tabIndex: -1 }} aria-hidden="true">
        <label htmlFor="website_url_hp">Website URL</label>
        <input
          id="website_url_hp"
          type="text"
          name="website_url"
          value={form.website_url}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Send Us a Message</h2>
      <p className="text-gray-400 text-sm mb-7">We&apos;ll respond within one business day.</p>

      {/* Success state */}
      {status === 'success' && (
        <div className="mb-6 bg-[#E8F5EE] border border-[#1B6B3A]/20 rounded-2xl p-6">
          <div className="flex flex-col items-center text-center mb-5">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1B6B3A] rounded-full mb-3">
              <i className="ri-check-line text-white text-2xl"></i>
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 mb-1">Message Sent!</h3>
            <p className="text-gray-500 text-sm">We&apos;ve received your inquiry and will be in touch soon.</p>
          </div>
          <div className="border-t border-[#1B6B3A]/15 pt-4 space-y-3">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">What happens next</p>
            {[
              { icon: 'ri-mail-check-line', text: 'You\'ll receive a confirmation email shortly' },
              { icon: 'ri-user-voice-line', text: 'A Greenly team member will review your request' },
              { icon: 'ri-phone-line', text: 'We\'ll contact you within 1 business day to follow up' },
            ].map((step) => (
              <div key={step.text} className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-[#1B6B3A]/10 rounded-full shrink-0 mt-0.5">
                  <i className={`${step.icon} text-[#1B6B3A] text-sm`}></i>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-[#1B6B3A]/15 flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-gray-500">Need a faster response?</p>
            <a href="tel:+12136001000" className="inline-flex items-center gap-2 text-[#1B6B3A] font-bold text-sm hover:underline cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill"></i> Call (213) 600-1000
            </a>
          </div>
        </div>
      )}

      {/* Server error */}
      {status === 'error' && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 rounded-xl px-5 py-4 flex items-center gap-3 text-sm">
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-error-warning-line text-lg"></i>
          </div>
          Something went wrong. Please try again or call us at (213) 600-1000.
        </div>
      )}

      {/* Name + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
            Full Name <span className="text-red-400">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={form.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="John Smith"
            className={inputClass(!!errors.name)}
          />
          <FieldError msg={errors.name} />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
            Email Address <span className="text-red-400">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="john@company.com"
            className={inputClass(!!errors.email)}
          />
          <FieldError msg={errors.email} />
        </div>
      </div>

      {/* Phone + Company */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
            Phone Number <span className="text-gray-400 font-normal text-xs">(optional)</span>
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="(213) 555-0100"
            className={inputClass(!!errors.phone)}
          />
          <FieldError msg={errors.phone} />
        </div>
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1.5">
            Company / Organization <span className="text-gray-400 font-normal text-xs">(optional)</span>
          </label>
          <input
            id="company"
            name="company"
            type="text"
            value={form.company}
            onChange={handleChange}
            placeholder="Your Company"
            className={inputClass(false)}
          />
        </div>
      </div>

      {/* Service */}
      <div className="mb-4">
        <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1.5">
          Service Needed <span className="text-red-400">*</span>
        </label>
        <select
          id="service"
          name="service"
          value={form.service}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`${inputClass(!!errors.service)} bg-white cursor-pointer`}
        >
          <option value="">Select a service...</option>
          {serviceOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <FieldError msg={errors.service} />
      </div>

      {/* Message */}
      <div className="mb-6">
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
          Message <span className="text-red-400">*</span>
          <span className={`float-right font-normal text-xs ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`}>
            {charCount}/500
          </span>
        </label>
        <textarea
          id="message"
          name="message"
          rows={4}
          maxLength={500}
          value={form.message}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Tell us about your e-waste recycling needs, quantity of items, location, etc."
          className={`${inputClass(!!errors.message)} resize-none`}
        />
        <FieldError msg={errors.message} />
      </div>

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full bg-[#1B6B3A] hover:bg-[#2D8653] disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
      >
        {status === 'loading' ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i> Sending...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i> Send Message
          </>
        )}
      </button>

      <p className="text-center text-xs text-gray-400 mt-4">
        By submitting this form, you agree to our{' '}
        <a href="/privacy-policy" className="underline hover:text-gray-600 cursor-pointer">privacy policy</a>.
        We never share your information.
      </p>
    </form>
  );
};

export default ContactForm;
